// two bobby: one-tap add for cart add-ons. Mirrors Horizon's product form flow (CartLinesUpdateEvent
// with a deferred promise, then cart/add with the cart sections) so the drawer, bubble and analytics
// update exactly as they do for a normal add.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

class TbAddButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#add);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#add);
  }

  #add = async () => {
    const id = this.dataset.variantId;
    if (!id || !this.button || this.button.getAttribute('aria-busy') === 'true') return;
    const qty = Math.max(1, Number(this.dataset.quantity) || 1);
    const removeKey = this.dataset.removeKey; // cart sold-out swap (22 Oct 2026): drop the dead line, then add
    const context = this.dataset.context || 'cart-addons';

    const label = this.button.textContent;
    this.button.setAttribute('aria-busy', 'true');
    this.button.textContent = 'Adding…';

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines: [{ merchandiseId: String(id), quantity: qty }],
        promise: deferred.promise,
      })
    );

    try {
      if (removeKey) {
        await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('json', { body: JSON.stringify({ id: removeKey, quantity: 0 }) }),
        });
      }
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }], sections }) }),
      }).then((r) => r.json());

      if (response.status) throw Object.assign(new Error(response.description || response.message), { response });

      const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, source: context === 'cart-swap' ? 'tb-cart-swap' : 'tb-cart-addons', itemCount: qty, sections: response.sections, didError: false },
      });
      // Buttons outside the cart drawer (product-page mini cards) are not re-rendered: confirm, then restore.
      this.button.textContent = 'Added';
      setTimeout(() => {
        if (!this.isConnected || !this.button) return;
        this.button.textContent = label;
        this.button.removeAttribute('aria-busy');
      }, 1500);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Could not add to cart', code: 'INVALID' }));
      this.button.textContent = label;
      this.button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('tb-add-button')) customElements.define('tb-add-button', TbAddButton);
